/** SA-1080 (spec §5.2, §5.3): what a routing save will change, shown in place of
 *  the Save row once the server has checked the policy. Presentational: the
 *  editor owns the check, the lines and the callbacks; every word is in
 *  `routerPolicyText`. */
import { useEffect, useId, useRef } from 'react'
import { Button } from '@/components/ui'
import { reviewWords } from './routerPolicyText'

/** The sentences for what adapting the policy to its channel left out, under their heading. */
export function LeftOutList({ heading, lines }: { heading: string; lines: string[] }) {
  if (lines.length === 0) return null
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-foreground">{heading}</p>
      <ul className="list-disc pl-5 space-y-0.5 text-sm text-warning-strong">
        {lines.map((line, index) => <li key={`${index}:${line}`}>{line}</li>)}
      </ul>
    </div>
  )
}

export interface SaveReviewProps {
  /** `policyChanges(current, policy, names)`, in its order. */
  lines: string[]
  leftOut: string[]
  leftOutHeading: string
  /** A mailbox that is not a router yet: Save these changes is the switch's confirmation. */
  switchWarning: string | null
  /** Why the review opened again (the stored policy moved after the check), or null. */
  notice: string | null
  /** The policy is the one already saved: only Back to editing is offered. */
  nothing: boolean
  onConfirm: () => void
  onBack: () => void
}

export function SaveReview({
  lines, leftOut, leftOutHeading, switchWarning, notice, nothing, onConfirm, onBack,
}: SaveReviewProps) {
  const headingId = useId()
  const noticeId = useId()
  const ref = useRef<HTMLElement>(null)
  // The button that opened the review is gone with the Save row; the review takes the focus.
  useEffect(() => { ref.current?.focus() }, [])
  return (
    <section ref={ref} aria-labelledby={headingId} aria-describedby={notice ? noticeId : undefined} tabIndex={-1}
      className="rounded-md border border-border bg-card p-3 space-y-3 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
      <h4 id={headingId} className="text-sm font-semibold text-foreground">{reviewWords.heading}</h4>
      {notice && <p id={noticeId} role="status" className="text-sm text-warning-strong">{notice}</p>}
      {switchWarning && <p className="text-sm text-warning-strong">{switchWarning}</p>}
      <LeftOutList heading={leftOutHeading} lines={leftOut} />
      {nothing ? (
        <p className="text-sm text-muted-foreground">{reviewWords.nothing}</p>
      ) : lines.length > 0 && (
        <ul className="list-disc pl-5 space-y-0.5 text-sm text-foreground">
          {lines.map((line, index) => <li key={`${index}:${line}`}>{line}</li>)}
        </ul>
      )}
      <div className="flex items-center gap-2">
        {!nothing && <Button type="button" size="sm" onClick={onConfirm}>{reviewWords.save}</Button>}
        <Button type="button" variant={nothing ? 'secondary' : 'ghost'} size="sm" onClick={onBack}>{reviewWords.back}</Button>
      </div>
    </section>
  )
}
